import { fetchExperienceCategories, fetchExperienceMetrics } from 'actions/ExperienceActions'
import HeadData from 'components/Common/HeadData'
import { getExperiencesSearchHeadMeta } from '../ExperiencesSearch/getExperiencesSearchHeadMeta'
import { useEffect } from 'react'
import { getExperienceCarouselHeadingText } from 'constants/experience'
import useQueryParams from 'hooks/useQueryParams'
import { waitElement } from 'lib/dom/waitElement'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import ExperiencesPageSearchHeader from './ExperiencesPageSearchHeader'
import { useGlobalExperiencesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalExperiencesSearch'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import useSmartExperiencePlace from '../hooks/useSmartExperiencePlace'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import ExperiencesHomepageUpliftLayout from './ExperiencesHomepageUplift/ExperiencesHomepageUpliftLayout'
import ExperiencesHomepageMetrics from './ExperiencesHomepageUplift/ExperiencesHomepageMetrics'
import ExperiencesHomepagePersonalisation from './ExperiencesHomepageUplift/ExperiencesHomepagePersonalisation'
import ExperiencesHomepageRecentSearches from './ExperiencesHomepageUplift/ExperiencesHomepageRecentSearches'
import ExperiencesHomepageLuxPlusTopOffers from './ExperiencesHomepageUplift/ExperiencesHomepageLuxPlusTopOffers'
import ExperiencesHomepageBrowseByCategory from './ExperiencesHomepageUplift/ExperiencesHomepageBrowseByCategory'
import ExperiencesHomepageBrowseByTrend from './ExperiencesHomepageUplift/ExperiencesHomepageBrowseByTrend'
import ExperiencesHomepageTopPicks from './ExperiencesHomepageUplift/ExperiencesHomepageTopPicks'
import ExperiencesHomepageDiscoverNear from './ExperiencesHomepageUplift/ExperiencesHomepageDiscoverNear'

function ExperiencesPage() {
  const intl = useIntl()
  const { title, description, imageAlt } = getExperiencesSearchHeadMeta({ isAnywhere: true, intl })
  const { globalExperiencesSearchDispatch, globalExperiencesSearchState } = useGlobalExperiencesSearchContext()

  const dispatch = useAppDispatch()
  const currentPlace = useSmartExperiencePlace()

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchExperienceMetrics())
  }, [dispatch])

  const queryParams = useQueryParams()
  const carouselName = queryParams.get('carousel')

  useEffect(() => {
    if (!carouselName) return

    const headingText = getExperienceCarouselHeadingText(intl, carouselName)
    if (!headingText) return

    const findHeadingByText = (text: string): HTMLElement | null => {
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
      while (walker.nextNode()) {
        if (walker.currentNode.textContent?.trim() === text) {
          return walker.currentNode.parentElement
        }
      }
      return null
    }

    const controller = new AbortController()
    waitElement(() => findHeadingByText(headingText), 15, 500)
      .then(el => {
        if (!controller.signal.aborted) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
      .catch(() => {}) // element not found after retries
    return () => controller.abort()
  }, [carouselName, intl])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
    <HeadData title={title} description={description} imageAlt={imageAlt} vertical="experiences" />
    <GlobalSearchContextProvider
      state={globalExperiencesSearchState}
      dispatch={globalExperiencesSearchDispatch}
    >
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>
      <ExperiencesPageSearchHeader />
      {currentPlace && (
        <ExperiencesHomepageUpliftLayout
          metrics={<ExperiencesHomepageMetrics />}
          personalisation={<ExperiencesHomepagePersonalisation />}
          recentSearches={<ExperiencesHomepageRecentSearches />}
          luxPlusTopOffers={<ExperiencesHomepageLuxPlusTopOffers />}
          browseByCategory={<ExperiencesHomepageBrowseByCategory />}
          browseByTrend={<ExperiencesHomepageBrowseByTrend />}
          topPicks={<ExperiencesHomepageTopPicks />}
          discoverNear={<ExperiencesHomepageDiscoverNear />}
        />
      )}
    </GlobalSearchContextProvider>
  </AnalyticsPageContext.Provider>
}

export default ExperiencesPage
